import {
  Stage,
  PerspectiveView,
  FloorModel,
  THREE,
  EventHub,
  PanoramaGroup,
  PanoramaController,
  IndicatorDisplayMode,
  TransformController,
  Events,
  MeshLine2D,
  PathUtils,
} from '@aibee/owlly';
import * as endArrowImg from '../../../assets/image/end-arrow.png';

function getArrowAngleByDir(dir) {
  const dx = new THREE.Vector3(1, 0, 0);
  const dd = new THREE.Vector3(dir.x, dir.y, 0).normalize();
  const angle = dx.clone().angleTo(dd);
  const t = dx.clone().dot(dd);
  // let tangle = Math.acos(t);
  // let angle2 = dx.clone().angleTo(dir);
  const dxc = dx.clone().cross(dd);
  if (dxc.z < 0) {
    return -angle + Math.PI;
  }
  return angle + Math.PI;
}

/**
 * 平滑路径
 * @param {Array<{x, y}>} path
 * @returns {Array}
 */
function SmoothPath(path) {
  const trailVertices = [];
  path.forEach((element) => {
    trailVertices.push([element.x, element.y, 0]);
  });
  // LogUtil.log(pano_ids);
  const simplified = [];
  const minZ = 0;
  trailVertices.forEach((point) => {
    simplified.push({ x: point[0], y: point[1] });
  });

  const simplifiedPath = PathUtils.simplify(simplified, true);
  const smoothedPath = PathUtils.smoothPath(simplifiedPath.map(p => ([p.x, p.y])));
  const smoothedPoints = [];

  smoothedPath.forEach((p) => {
    smoothedPoints.push([p[0], p[1], minZ]);
  });
  return smoothedPoints;
}
class OwllyStage {
  constructor(payload) {
    this.payload = payload;

    this.lines = [];
    this.linesDrag = [];
    this.pathLine = null;
    this.pathColor = 0x0000ff;
    this.pathWidth = 1;
    this.eventListeners = {
      onPanoClick: (pid, event) => {
        this.onPanoClick(pid, event);
      },
      onMoveToPanorama: (pid) => {
        this.onMoveToPanorama(pid);
      },
      onPanoramaCameraChanged: (e, p) => {
        if (this.payload.listeners.onPanoramaCameraChanged) {
          this.payload.listeners.onPanoramaCameraChanged(p);
        }
      },
    };
  }

  onMoveToPanorama(pid) {
    this.currentPid = pid;
    if (this.payload.listeners.onMoveToPanorama) {
      this.payload.listeners.onMoveToPanorama(pid);
    }
  }

  /**
   * 设置路径
   * @param {Array} path 路径
   * @param {String} textureUrl 纹理路径
   * @param {number} pathHeight 路径高度
   * @param {number} color 路径颜色 16进制数据
   * @param {number} width 路径宽度
   */
  setPath(path, textureUrl, pathHeight = -1.7, color = 0x0000ff, width) {
    const { stage, pathColor, pathWidth } = this;
    let { pathLine } = this;
    // LogUtil.log('path', path);
    if (color == null) {
    // eslint-disable-next-line no-param-reassign
      color = pathColor;
    }
    if (width == null) {
    // eslint-disable-next-line no-param-reassign
      width = pathWidth;
    }
    if (pathLine) {
      stage.disposeElement(pathLine);
    }
    const smoothedPoints = SmoothPath(path);
    pathLine = new MeshLine2D(smoothedPoints, {
      textureUrl,
      // eslint-disable-next-line radix
      color: parseInt(color),
      current: 0,
      viewDistance: 8,
      gradientDistance: 5,
      gradientEnabled: true,
      width,
    // animateEnabled: true,
    // animateSpeed: 0.02,
    });
    this.pathLine = pathLine;
    pathLine.setCurrent(0);
    // pathLine.uniforms.gradientDistance.value = 2;

    pathLine.position.z = pathHeight;
    // LogUtil.log('pathLine', pathLine, pathHeight);
    stage.bindElement(pathLine);
    return pathLine;
  }

  async init() {
    const { container, panoramaList, modelPath, initialView } = this.payload;

    this.stage = new Stage();
    this.view = new PerspectiveView(container);
    const model = new FloorModel(modelPath);
    this.stage.bindElement(model);
    this.panoramaGroup = new PanoramaGroup(panoramaList.map(p => ({
      pid: p.id,
      seqID: p.seq_id,
      textureURLs: JSON.parse(p.panorama_images_low),
      neighbourPanoramaIDs: p.neighbourPanoramaIDs,
      position: new THREE.Vector3(
        p.coordinate.position_x, p.coordinate.position_y, p.coordinate.position_z,
      ),
      quaternion: new THREE.Quaternion(
        p.coordinate.direction_x, p.coordinate.direction_y, p.coordinate.direction_z, p.coordinate.direction_w,
      ),
    })), {
      modelElement: model,
      groundParams: {
        downDirection: new THREE.Vector3(0, 0, -1),
      },
    });
    this.draggingPanoramaGroup = new PanoramaGroup([], {
      modelElement: model,
      groundParams: {
        downDirection: new THREE.Vector3(0, 0, -1),
      },
    });

    this.panoramaController = new PanoramaController(this.view, this.panoramaGroup, false);
    this.panoramaController.setFOVRange(1, 150); // 设置视角范围
    this.transformCtrl = new TransformController(this.view);
    this.transformCtrl.set2DElementDetectionRadius(30);
    this.stage.bindController(this.transformCtrl);
    this.stage.bindView(this.view);
    this.stage.bindElement(this.panoramaGroup);
    this.stage.bindController(this.panoramaController);

    this.panoramaController.setMouseClickEnabled(true);
    this.panoramaController.setMoveOnClickEnabled(false);
    this.panoramaController.setOnScreenDetectRadius(20);

    this.panoramaGroup.addViewConfiguration({
      targetView: this.view,
      showBorder: false,
      showSkyBox: true,
    });
    this.panoramaGroup.addGroupViewConfiguration({
      targetView: this.view,
      indicatorDisplayMode: IndicatorDisplayMode.Show,
    });
    await this.panoramaController.lookFromPanoramaTo(panoramaList[0].id, new THREE.Vector3(
      initialView.roaming_direction_x,
      initialView.roaming_direction_y,
      initialView.roaming_direction_z,
    ));

    this.currentPid = panoramaList[0].id;
    this.onMoveToPanorama(panoramaList[0].id);

    this.bindEvent();
  }

  clearPaths() {
    this.lines.forEach((l) => {
      this.stage.disposeElement(l);
    });
    this.lines = [];
  }

  clearDraggingPaths() {
    this.linesDrag.forEach((l) => {
      this.stage.disposeElement(l);
    });
    this.linesDrag = [];
  }

  addPath(path) {
    const line = new MeshLine2D(
      path,
      {
        gradientEnabled: false,
        color: '#ff0000',
      },
    );
    line.position.z = -1.7;
    this.stage.bindElement(line);
    this.lines.push(line);
  }

  moveToPanoramaDirection(pid, direction, fov = 120) {
    const data = {
      intervals: 1000,
      toDirections: direction,
      fovs: fov,
      showModel: false,
    };
    this.panoramaController.moveInPath([pid], data);
  }

  showModel(pos, npos) {
    const width = 2;
    const color = 0x0000ff;
    const pathHeight = -1.7;
    const ge = new THREE.PlaneGeometry(width * 2, width);
    const img = new Image();
    const material = new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(ge, material);
    mesh.renderOrder = 10002;
    mesh.opacity = 0;
    mesh.rotation.set(Math.PI / 2, 0, 0);
    mesh.position.set(0, 0, pathHeight + width);
    this.stage.scene.add(mesh);
    img.onload = () => {
      const texture = new THREE.Texture(img);
      texture.needsUpdate = true;
      material.map = texture;
      material.needsUpdate = true;
      mesh.opacity = 1;
    };
    img.src = endArrowImg;
    const pathEndArrow = mesh;

    // 设置角度和位置
    let moveDir = null;
    if (npos) {
      moveDir = new THREE.Vector2(pos.x - npos.x, pos.y - npos.y); // 移动方向Vector2|Vector3
    } else {
      moveDir = new THREE.Vector2(1, 0); // 移动方向Vector2|Vector3
    }
    const offsetDis = 1;
    console.log(moveDir);
    pathEndArrow.rotation.set(pathEndArrow.rotation.x, getArrowAngleByDir(moveDir), 0);
    const tDir = new THREE.Vector2(moveDir.x, moveDir.y).normalize();
    tDir.rotateAround(new THREE.Vector2(0, 0), Math.PI / 2);
    tDir.multiplyScalar(offsetDis);
    pathEndArrow.position.set(pos.x + tDir.x, pos.y + tDir.y, pathEndArrow.position.z);
    console.log(pathEndArrow);
  }

  addDraggingPaths(path) {
    const line = new MeshLine2D(
      path,
      {
        gradientEnabled: false,
        color: '#ff0000',
      },
    );
    line.position.z = -1.7;
    this.stage.bindElement(line);
    this.linesDrag.push(line);
  }

  moveToPanorama(pid, fade = true) {
    if (this.panoramaController) {
      this.panoramaController.moveToPanorama(pid, {
        fadeInOut: fade,
      });
    }
  }

  onPanoClick(pid, event) {
    if (event.button === 0) {
      setTimeout(() => {
        this.currentPid = pid;
        this.moveToPanorama(pid, false);
      }, 500);
    }
  }

  resize() {
    this.stage.forceResize();
  }

  bindEvent() {
    EventHub.on(Events.CLICK_PANORAMA, this.eventListeners.onPanoClick);
    EventHub.on(Events.MOVE_TO_PANORAMA, this.eventListeners.onMoveToPanorama);
    EventHub.on(Events.PANORAMA_CAMERA_CHANGED, this.eventListeners.onPanoramaCameraChanged);
  }

  unbindEvent() {
    EventHub.remove(Events.CLICK_PANORAMA, this.eventListeners.onPanoClick);
    EventHub.remove(Events.MOVE_TO_PANORAMA, this.eventListeners.onMoveToPanorama);
    EventHub.remove(Events.PANORAMA_CAMERA_CHANGED, this.eventListeners.onPanoramaCameraChanged);
  }

  dispose() {
    if (this.stage) {
      this.stage.dispose();
    }
    this.unbindEvent();
  }
}

export default OwllyStage;
